import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';
import {
  BookOpen,
  Sparkles,
  CheckCircle,
  ChevronRight,
  Users,
  Zap,
  ShieldCheck,
  Award,
  Heart,
  Star,
  Languages,
  GraduationCap,
  X
} from 'lucide-react';

const MASCOT_URL =
  'https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png';

const FOUNDER_PHOTO_URL =
  'https://customer-assets-39nsmqrw.emergentagent.net/job_scaly-tutor/artifacts/3emhmxgr_96a27dd6872578e7d90b550bab03d6cf4592e0c10adba9421f89616f6fde9aaa.webp';

const FOUNDER_CERT_URL =
  'https://customer-assets.emergentagent.com/job_scaly-tutor/artifacts/icuxd5or_file_000000005e247207b7f76c4cd2fcd0f0.jpg';

// ---- Translations (English / Simplified Chinese) ----
const T = {
  en: {
    langLabel: 'English',
    login: 'Log in',
    startFree: 'Start free',
    moeBadge: 'Aligned to Singapore MOE syllabus',
    foundingBannerA: '🎉 Founding Members Offer',
    foundingBannerB: 'First 1,000 sign-ups get UNLIMITED practice questions for 1 year',
    foundingBannerC: 'for just SGD 2 — for the level you subscribe to.',
    foundingBannerCta: 'Claim now',
    foundingBannerSpotsLeft: 'spots left',
    foundingBannerUrgent: '⚠️ Almost sold out',
    foundingBannerSoldOutA: '🎉 Founding Members SOLD OUT',
    foundingBannerSoldOutB: 'Thank you! Regular pricing now applies — join Prof.Scaly today for daily practice.',
    foundingBannerSoldOutCta: 'Sign up',
    heroTitle1: 'Prof.Scaly makes',
    heroBrand: 'PSLE English',
    heroTitle2: 'practice fun.',
    heroSubtitle: 'A friendly AI tutor for Primary 4, 5 and 6 students. Nine PSLE English modules, instant kid-friendly explanations, and progress tracking parents actually understand.',
    ctaRegister: 'Start free — no card required',
    ctaDemo: 'Try a demo question',
    trust50Free: '50 free per topic',
    trustLevels: 'P4 · P5 · P6',
    trustAI: 'Instant AI tutor',
    howTitle: 'How it works',
    howSubtitle: "Three simple steps to build your child's PSLE English confidence.",
    step1Num: 'STEP 1', step1Title: 'Sign up in 30 seconds', step1Desc: "Create a free account and choose your child's level — Primary 4, 5 or 6.",
    step2Num: 'STEP 2', step2Title: 'Pick any of 9 topics', step2Desc: 'From Grammar Cloze to Antonyms & Synonyms — each aligned to the MOE syllabus.',
    step3Num: 'STEP 3', step3Title: 'Practise with instant AI feedback', step3Desc: 'Every wrong answer comes with a kid-friendly explanation and follow-up questions.',
    modulesTitle: '9 PSLE English modules',
    modulesSubtitle: 'Every question type in the PSLE English paper — plus dedicated modules for the sub-skills that trip students up most.',
    demoBadge: 'Try before you sign up',
    demoTitle: 'A real PSLE-style question',
    demoSubtitle: "Pick an answer and see Prof.Scaly's explanation instantly.",
    demoLabel: 'Grammar MCQ · Primary 5',
    demoQuestion: 'By the time we reached the station, the train ______ already left.',
    demoCorrectHeader: '🎉 Well done!',
    demoWrongHeader: "Good try — here's the trick:",
    demoExplanation: "'Had' is the past perfect tense — used when one past action (the train leaving) happened before another past action (we arrived). 'Has' is present perfect and 'have' doesn't agree with the singular 'train'.",
    demoCtaMore: 'Get 100s more like this — free',
    pricingTitle: 'Simple, honest pricing',
    pricingSubtitle: 'Start free. Upgrade only when your child is ready to go unlimited.',
    priceFreeLabel: 'FREE', priceFreePer: '/forever', priceFreeDesc: 'Perfect for trying Prof.Scaly.',
    freeFeat1: '50 free questions per topic', freeFeat2: 'All 9 PSLE modules', freeFeat3: 'AI tutor explanations', freeFeat4: 'Daily streaks & progress tracking',
    priceFreeCta: 'Start free',
    priceRecommended: 'RECOMMENDED',
    pricePremiumLabel: 'PREMIUM', pricePremiumAmount: 'SGD $2', pricePremiumPer: '/year per level',
    pricePremiumDesc: 'Less than a plate of chicken rice.',
    premFeat1: 'Everything in Free', premFeat2: 'Unlimited practice questions', premFeat3: 'Independent sub-skill progress', premFeat4: 'Priority support',
    pricePremiumCta: 'Get Premium',
    testimonialsTitle: 'Loved by parents and students',
    testimonialsSubtitle: 'Early reviews from families using Prof.Scaly.',
    testSlot1Name: 'Parent · P5', testSlot1Text: 'Add a real parent testimonial here after your first month of users.',
    testSlot2Name: 'Student · P6', testSlot2Text: 'Add a student quote here — kids love the Prof.Scaly mascot.',
    testSlot3Name: 'Tuition Teacher', testSlot3Text: 'Add a teacher testimonial about the custom question upload feature.',
    testSlotRole: '(Testimonial slot)',
    faqTitle: 'Questions parents ask',
    faqSubtitle: 'Still unsure? Tap Prof.Scaly at the bottom-right to chat.',
    finalCtaTitle: 'Ready to make PSLE English fun?',
    finalCtaSub: 'Create a free account in 30 seconds — no credit card required.',
    finalCtaBtn: 'Start free with Prof.Scaly',
    footerAbout: 'PSLE English practice for Singapore Primary 4, 5 & 6 students. Aligned to the MOE syllabus.',
    footerProduct: 'Product', footerCompany: 'Company', footerTrust: 'Trust',
    footerStart: 'Start free', footerLogin: 'Log in', footerDemo: 'Demo question',
    footerContact: 'Contact', footerPrivacy: 'Privacy', footerTerms: 'Terms',
    trustSecure: 'Secure by design', trustMoe: 'MOE-aligned', trustBuilt: 'Built with love in SG',
    footerRights: 'All rights reserved.',
    disclaimer: 'Prof.Scaly is an independently developed educational platform and is not affiliated with, endorsed by, or connected to Singapore\'s Ministry of Education (MOE) or the Singapore Examinations and Assessment Board (SEAB).',
    founderTitle: 'Meet the founder',
    founderSubtitle: 'The educator behind Prof.Scaly.',
    founderName: 'Mr Victor Chan',
    founderRole: 'Founder · English Tutor · Singaporean',
    founderQualBadge: 'Graduate Diploma in TESOL · 2019',
    founderInstitution: 'London Teacher Training College',
    founderBio1: 'Victor is a Singaporean who was born, raised and educated in Singapore. From his school days he was an avid reader, and it was his love of reading that led him to fall in love with the English language itself.',
    founderBio2: 'In 2019, Victor decided to pursue professional teaching certification under the London Teacher Training College, and in that same year earned his Graduate Diploma in TESOL (Teaching English to Speakers of Other Languages).',
    founderBio3: 'Since then he has taught a number of Primary and Secondary school students across Singapore, blending the MOE English syllabus with wider reading materials to help students build real confidence — not just exam skills.',
    founderCertCaption: 'LTTC — Graduate Diploma in TESOL',
    founderCertCta: 'View certificate',
    founderCertClose: 'Close',
    modules: ['Grammar Cloze', 'Vocabulary Cloze', 'Grammar MCQ', 'Vocabulary MCQ', 'Synthesis & Transformation', 'Editing', 'Comprehension Cloze', 'Antonyms & Synonyms', 'Regular & Irregular Verbs'],
    faq: [
      { q: 'Is Prof.Scaly aligned to the Singapore MOE syllabus?', a: 'Yes. Every practice question is generated (or curated by teachers) specifically for the PSLE English format — Grammar Cloze, Vocabulary Cloze, Grammar MCQ, Vocabulary MCQ, Synthesis & Transformation, Editing, Comprehension Cloze, plus dedicated modules for Antonyms & Synonyms and Regular & Irregular Verbs.' },
      { q: 'Which primary levels does it support?', a: 'Primary 4, Primary 5 and Primary 6. Questions are calibrated to the difficulty of each level, and progress is tracked separately for each topic and sub-skill.' },
      { q: 'How does the AI generate questions?', a: 'We use GPT-5.2 with carefully crafted prompts written by English tutors. Every question is scoped to the PSLE format and level. When your child gets a question wrong, they can tap "Explain Further" to ask Prof.Scaly for a kid-friendly walk-through — a private mini-tutor at every seat.' },
      { q: 'Can teachers or tutors upload their own questions?', a: 'Yes. Teachers and administrators can upload questions via a form or CSV bulk upload. Uploaded questions mix 50/50 with AI-generated questions during practice, so students see a mix of curated and fresh content.' },
      { q: 'How much does it cost? Can I cancel any time?', a: "Every student gets 50 free practice questions per topic — that's hundreds of free questions across all 9 modules. Upgrade to unlimited practice at one Primary level for just SGD $2 per year. You can cancel at any time from your account." }
    ]
  },
  zh: {
    langLabel: 'EN',
    login: '登录',
    startFree: '免费开始',
    moeBadge: '符合新加坡教育部课程大纲',
    foundingBannerA: '🎉 创始会员优惠',
    foundingBannerB: '前1,000名注册者可享一整年无限次练习题',
    foundingBannerC: '仅需新币 SGD 2 — 适用于您所订阅的年级。',
    foundingBannerCta: '立即抢购',
    foundingBannerSpotsLeft: '个名额',
    foundingBannerUrgent: '⚠️ 即将售罄',
    foundingBannerSoldOutA: '🎉 创始会员名额已满',
    foundingBannerSoldOutB: '感谢支持!现已恢复常规价格 — 立即加入 Prof.Scaly 开始练习。',
    foundingBannerSoldOutCta: '注册',
    heroTitle1: 'Prof.Scaly 让',
    heroBrand: '小六会考英文',
    heroTitle2: '练习变得有趣。',
    heroSubtitle: '专为小四、小五、小六学生打造的友善AI导师。九大英文题型模块、即时的儿童友好解答,以及家长看得懂的学习进度追踪。',
    ctaRegister: '免费开始 — 无需信用卡',
    ctaDemo: '试做示范题',
    trust50Free: '每题型50题免费',
    trustLevels: '小四 · 小五 · 小六',
    trustAI: '即时AI导师',
    howTitle: '使用步骤',
    howSubtitle: '三个简单步骤,建立孩子的小六会考英文信心。',
    step1Num: '第一步', step1Title: '30秒注册', step1Desc: '免费创建账号,选择孩子的年级 — 小四、小五或小六。',
    step2Num: '第二步', step2Title: '选择九大题型', step2Desc: '从语法填空到反义词与同义词 — 全部符合教育部课程大纲。',
    step3Num: '第三步', step3Title: '获得即时AI反馈', step3Desc: '每答错一题都有儿童友好的讲解和后续练习题。',
    modulesTitle: '九大 PSLE 英文模块',
    modulesSubtitle: '涵盖小六会考英文试卷的每一种题型 — 再加上专为常见弱点设计的独立训练模块。',
    demoBadge: '注册前先试试',
    demoTitle: '真实的 PSLE 风格题目',
    demoSubtitle: '选择答案,立即查看 Prof.Scaly 的讲解。',
    demoLabel: '语法选择题 · 小五',
    demoQuestion: 'By the time we reached the station, the train ______ already left.',
    demoCorrectHeader: '🎉 太棒了!',
    demoWrongHeader: '好尝试 — 让我来解释一下:',
    demoExplanation: "『Had』是过去完成时(past perfect tense),用于一个过去的动作(火车离开)发生在另一个过去动作(我们到达)之前。『Has』是现在完成时,而『have』与单数的『train』主语不匹配。",
    demoCtaMore: '获得数百道类似题目 — 完全免费',
    pricingTitle: '简单透明的价格',
    pricingSubtitle: '从免费开始,准备好时再升级到无限练习。',
    priceFreeLabel: '免费版', priceFreePer: '/永久', priceFreeDesc: '适合体验 Prof.Scaly。',
    freeFeat1: '每题型50题免费', freeFeat2: '全部9个模块', freeFeat3: 'AI导师讲解', freeFeat4: '每日打卡与进度追踪',
    priceFreeCta: '免费开始',
    priceRecommended: '推荐',
    pricePremiumLabel: '高级版', pricePremiumAmount: '新币 $2', pricePremiumPer: '/每年/每年级',
    pricePremiumDesc: '比一盘鸡饭还便宜。',
    premFeat1: '免费版全部功能', premFeat2: '无限练习题目', premFeat3: '独立子技能进度追踪', premFeat4: '优先技术支持',
    pricePremiumCta: '升级高级版',
    testimonialsTitle: '深受家长与学生喜爱',
    testimonialsSubtitle: '来自使用 Prof.Scaly 家庭的早期评价。',
    testSlot1Name: '家长 · 小五', testSlot1Text: '第一个月上线后,请在此处填入真实家长的评价。',
    testSlot2Name: '学生 · 小六', testSlot2Text: '在此加入学生的话 — 孩子们都喜欢 Prof.Scaly 吉祥物。',
    testSlot3Name: '补习老师', testSlot3Text: '关于自定义题库上传功能的老师评价可加入此处。',
    testSlotRole: '(评价占位)',
    faqTitle: '家长常见问题',
    faqSubtitle: '还不确定?点击右下角的 Prof.Scaly 与我们聊聊。',
    finalCtaTitle: '准备好让小六会考英文变得有趣了吗?',
    finalCtaSub: '30秒内创建免费账号 — 无需信用卡。',
    finalCtaBtn: '免费使用 Prof.Scaly',
    footerAbout: '专为新加坡小四、小五、小六学生打造的 PSLE 英文练习。符合教育部课程大纲。',
    footerProduct: '产品', footerCompany: '公司', footerTrust: '信任',
    footerStart: '免费开始', footerLogin: '登录', footerDemo: '示范题',
    footerContact: '联系我们', footerPrivacy: '隐私政策', footerTerms: '服务条款',
    trustSecure: '安全设计', trustMoe: '符合教育部标准', trustBuilt: '在新加坡用心打造',
    footerRights: '版权所有。',
    disclaimer: 'Prof.Scaly 是一个独立开发的教育平台,与新加坡教育部(MOE)或新加坡考试与评估局(SEAB)没有任何隶属或认可关系。',
    founderTitle: '认识创办人',
    founderSubtitle: 'Prof.Scaly 背后的教育者。',
    founderName: '陈老师(Mr Victor Chan)',
    founderRole: '创办人 · 英文导师 · 新加坡人',
    founderQualBadge: 'TESOL 研究生文凭 · 2019',
    founderInstitution: '伦敦教师培训学院(LTTC)',
    founderBio1: 'Victor 是土生土长的新加坡人,自小便是一位热爱阅读的学生。他对阅读的热爱最终引领他爱上英文这门语言本身。',
    founderBio2: '2019年,Victor 决定报读伦敦教师培训学院(London Teacher Training College)的专业教师认证课程,并在同年取得了 TESOL(教授非英语母语者英文)研究生文凭。',
    founderBio3: '自此他辅导了新加坡多位小学与中学学生,把新加坡教育部的英文课程大纲与更广泛的阅读材料结合,帮助学生真正建立自信 — 不只是应付考试。',
    founderCertCaption: 'LTTC — TESOL 研究生文凭',
    founderCertCta: '查看证书',
    founderCertClose: '关闭',
    modules: ['语法填空', '词汇填空', '语法选择题', '词汇选择题', '句子改写与转换', '错误订正', '篇章填空', '反义词与同义词', '规则与不规则动词'],
    faq: [
      { q: 'Prof.Scaly 是否符合新加坡教育部课程大纲?', a: '是的。每一道练习题都是针对 PSLE 英文格式生成或由老师精选的 — 涵盖语法填空、词汇填空、语法选择题、词汇选择题、句子改写与转换、错误订正、篇章填空,加上专门的反义词与同义词、规则与不规则动词模块。' },
      { q: '支持哪些小学年级?', a: '小四、小五和小六。题目难度按照各年级校准,进度按照每个题型和子技能分别追踪。' },
      { q: 'AI 是如何生成题目的?', a: '我们使用 GPT-5.2,配合英文导师精心编写的提示词。每一道题都严格符合 PSLE 格式和年级。孩子答错时可以点击「进一步解释」,请 Prof.Scaly 用儿童友好的方式讲解 — 就像每张座位旁边都有一位私人小导师。' },
      { q: '老师或补习老师可以上传自己的题目吗?', a: '可以。老师和管理员可以通过表格或 CSV 批量上传题目。上传的题目会与 AI 生成的题目以50/50比例混合出现,让学生看到精选内容与新鲜题目并存。' },
      { q: '价格是多少?可以随时取消吗?', a: '每位学生每个题型可以免费练习50题 — 全部9个模块加起来就是数百道免费题目。升级到单一年级的无限练习每年只需新币 $2。您可以随时在账户中取消。' }
    ]
  },
  th: {
    langLabel: 'ภาษาไทย',
    login: 'เข้าสู่ระบบ',
    startFree: 'เริ่มฟรี',
    moeBadge: 'สอดคล้องกับหลักสูตร MOE สิงคโปร์',
    foundingBannerA: '🎉 โปรโมชั่นสมาชิกผู้ก่อตั้ง',
    foundingBannerB: '1,000 คนแรกที่สมัคร รับสิทธิ์ฝึกทำโจทย์ไม่จำกัด นาน 1 ปีเต็ม',
    foundingBannerC: 'เพียง SGD 2 — สำหรับระดับชั้นที่คุณสมัครสมาชิก',
    foundingBannerCta: 'รับสิทธิ์ตอนนี้',
    foundingBannerSpotsLeft: 'สิทธิ์เหลือ',
    foundingBannerUrgent: '⚠️ ใกล้เต็มแล้ว',
    foundingBannerSoldOutA: '🎉 สมาชิกผู้ก่อตั้งเต็มแล้ว',
    foundingBannerSoldOutB: 'ขอบคุณ! กลับสู่ราคาปกติ — สมัคร Prof.Scaly เพื่อฝึกทำโจทย์ทุกวัน',
    foundingBannerSoldOutCta: 'สมัคร',
    heroTitle1: 'Prof.Scaly ทำให้',
    heroBrand: 'ภาษาอังกฤษ PSLE',
    heroTitle2: 'สนุกยิ่งขึ้น',
    heroSubtitle: 'ติวเตอร์ AI สุดเป็นมิตรสำหรับนักเรียนประถม 4, 5 และ 6 — 9 หัวข้อ PSLE พร้อมคำอธิบายที่เข้าใจง่ายและระบบติดตามผลที่ผู้ปกครองเข้าใจได้',
    ctaRegister: 'เริ่มฟรี — ไม่ต้องใช้บัตรเครดิต',
    ctaDemo: 'ลองทำโจทย์ตัวอย่าง',
    trust50Free: 'ฟรี 50 ข้อต่อหัวข้อ',
    trustLevels: 'ป.4 · ป.5 · ป.6',
    trustAI: 'ติวเตอร์ AI ทันที',
    howTitle: 'ใช้งานอย่างไร',
    howSubtitle: 'สามขั้นตอนง่ายๆ เพื่อเสริมสร้างความมั่นใจในภาษาอังกฤษ PSLE ของบุตรหลาน',
    step1Num: 'ขั้นตอนที่ 1', step1Title: 'สมัครใน 30 วินาที', step1Desc: 'สร้างบัญชีฟรีและเลือกระดับชั้นของบุตรหลาน — ป.4, ป.5 หรือ ป.6',
    step2Num: 'ขั้นตอนที่ 2', step2Title: 'เลือกจาก 9 หัวข้อ', step2Desc: 'ตั้งแต่ Grammar Cloze ถึง Antonyms & Synonyms — สอดคล้องกับหลักสูตร MOE ทั้งหมด',
    step3Num: 'ขั้นตอนที่ 3', step3Title: 'ฝึกฝนพร้อม AI feedback ทันที', step3Desc: 'ทุกคำตอบที่ผิดจะได้รับคำอธิบายเข้าใจง่ายและโจทย์ตัวอย่างเพิ่มเติม',
    modulesTitle: '9 หัวข้อภาษาอังกฤษ PSLE',
    modulesSubtitle: 'ครอบคลุมทุกประเภทคำถามในข้อสอบ PSLE — พร้อมโมดูลเฉพาะสำหรับทักษะย่อยที่นักเรียนมักผิดพลาด',
    demoBadge: 'ลองก่อนสมัคร',
    demoTitle: 'โจทย์แนว PSLE จริง',
    demoSubtitle: 'เลือกคำตอบและดูคำอธิบายจาก Prof.Scaly ทันที',
    demoLabel: 'Grammar MCQ · ป.5',
    demoQuestion: 'By the time we reached the station, the train ______ already left.',
    demoCorrectHeader: '🎉 เก่งมาก!',
    demoWrongHeader: 'ลองดีแล้ว — มาดูเคล็ดลับกัน:',
    demoExplanation: "『Had』เป็น past perfect tense — ใช้เมื่อการกระทำในอดีตหนึ่ง (รถไฟออก) เกิดก่อนอีกการกระทำในอดีต (เราไปถึง) 『Has』เป็น present perfect และ 『have』ไม่สอดคล้องกับประธานเอกพจน์ 『train』",
    demoCtaMore: 'รับโจทย์ทำนองเดียวกันอีกหลายร้อยข้อ — ฟรี',
    pricingTitle: 'ราคาที่โปร่งใสและเรียบง่าย',
    pricingSubtitle: 'เริ่มฟรี อัพเกรดเมื่อบุตรหลานพร้อมสำหรับการฝึกไม่จำกัด',
    priceFreeLabel: 'ฟรี', priceFreePer: '/ตลอดไป', priceFreeDesc: 'เหมาะสำหรับทดลองใช้ Prof.Scaly',
    freeFeat1: 'ฟรี 50 ข้อต่อหัวข้อ', freeFeat2: 'ครบทั้ง 9 โมดูล PSLE', freeFeat3: 'คำอธิบายจาก AI ติวเตอร์', freeFeat4: 'เช็คอินรายวันและติดตามผล',
    priceFreeCta: 'เริ่มฟรี',
    priceRecommended: 'แนะนำ',
    pricePremiumLabel: 'พรีเมียม', pricePremiumAmount: 'SGD $2', pricePremiumPer: '/ปี/ระดับชั้น',
    pricePremiumDesc: 'ถูกกว่าข้าวมันไก่หนึ่งจาน',
    premFeat1: 'ทุกอย่างในแบบฟรี', premFeat2: 'ฝึกฝนไม่จำกัดจำนวน', premFeat3: 'ติดตามทักษะย่อยแบบแยกอิสระ', premFeat4: 'ซัพพอร์ตแบบเร่งด่วน',
    pricePremiumCta: 'สมัครพรีเมียม',
    testimonialsTitle: 'เป็นที่รักของผู้ปกครองและนักเรียน',
    testimonialsSubtitle: 'รีวิวแรกๆ จากครอบครัวที่ใช้ Prof.Scaly',
    testSlot1Name: 'ผู้ปกครอง · ป.5', testSlot1Text: 'เพิ่มรีวิวจริงของผู้ปกครองในที่นี้หลังจากใช้งานเดือนแรก',
    testSlot2Name: 'นักเรียน · ป.6', testSlot2Text: 'เพิ่มความคิดเห็นของนักเรียนในที่นี้ — เด็กๆ รักมาสคอต Prof.Scaly',
    testSlot3Name: 'คุณครูสอนพิเศษ', testSlot3Text: 'เพิ่มรีวิวคุณครูเกี่ยวกับฟีเจอร์อัพโหลดคำถามในที่นี้',
    testSlotRole: '(ช่องรีวิว)',
    faqTitle: 'คำถามที่ผู้ปกครองถามบ่อย',
    faqSubtitle: 'ยังไม่แน่ใจ? แตะ Prof.Scaly ที่มุมขวาล่างเพื่อพูดคุย',
    finalCtaTitle: 'พร้อมทำให้ภาษาอังกฤษ PSLE สนุกขึ้นแล้วหรือยัง?',
    finalCtaSub: 'สร้างบัญชีฟรีใน 30 วินาที — ไม่ต้องใช้บัตรเครดิต',
    finalCtaBtn: 'เริ่มฟรีกับ Prof.Scaly',
    footerAbout: 'การฝึกภาษาอังกฤษ PSLE สำหรับนักเรียนประถม 4, 5 และ 6 ในสิงคโปร์ สอดคล้องกับหลักสูตร MOE',
    footerProduct: 'ผลิตภัณฑ์', footerCompany: 'บริษัท', footerTrust: 'ความน่าเชื่อถือ',
    footerStart: 'เริ่มฟรี', footerLogin: 'เข้าสู่ระบบ', footerDemo: 'โจทย์ตัวอย่าง',
    footerContact: 'ติดต่อเรา', footerPrivacy: 'ความเป็นส่วนตัว', footerTerms: 'ข้อกำหนด',
    trustSecure: 'ปลอดภัยโดยการออกแบบ', trustMoe: 'สอดคล้องกับ MOE', trustBuilt: 'สร้างขึ้นด้วยรักในสิงคโปร์',
    footerRights: 'สงวนลิขสิทธิ์',
    disclaimer: 'Prof.Scaly เป็นแพลตฟอร์มการศึกษาที่พัฒนาขึ้นอย่างอิสระ และไม่ได้มีส่วนเกี่ยวข้องหรือรับการรับรองจาก MOE หรือ SEAB',
    founderTitle: 'พบกับผู้ก่อตั้ง',
    founderSubtitle: 'นักการศึกษาผู้อยู่เบื้องหลัง Prof.Scaly',
    founderName: 'คุณวิกเตอร์ ชาน (Mr Victor Chan)',
    founderRole: 'ผู้ก่อตั้ง · ติวเตอร์ภาษาอังกฤษ · ชาวสิงคโปร์',
    founderQualBadge: 'Graduate Diploma in TESOL · 2019',
    founderInstitution: 'London Teacher Training College',
    founderBio1: 'วิกเตอร์เป็นชาวสิงคโปร์ที่เกิด เติบโต และได้รับการศึกษาในสิงคโปร์ ตั้งแต่วัยเรียนเขาก็เป็นนักอ่านตัวยง และความรักในการอ่านนี่เองที่ทำให้เขาหลงรักภาษาอังกฤษ',
    founderBio2: 'ในปี 2019 วิกเตอร์ตัดสินใจเข้ารับการรับรองการสอนอย่างมืออาชีพจาก London Teacher Training College และในปีเดียวกันนั้นเขาได้รับ Graduate Diploma in TESOL (การสอนภาษาอังกฤษให้ผู้พูดภาษาอื่น)',
    founderBio3: 'ตั้งแต่นั้นมาเขาได้สอนนักเรียนประถมและมัธยมหลายคนทั่วสิงคโปร์ โดยผสมผสานหลักสูตร MOE เข้ากับสื่อการอ่านที่กว้างขึ้น เพื่อช่วยให้นักเรียนสร้างความมั่นใจอย่างแท้จริง — ไม่ใช่แค่ทักษะการทำข้อสอบ',
    founderCertCaption: 'LTTC — Graduate Diploma in TESOL',
    founderCertCta: 'ดูใบประกาศ',
    founderCertClose: 'ปิด',
    modules: ['Grammar Cloze', 'Vocabulary Cloze', 'Grammar MCQ', 'Vocabulary MCQ', 'Synthesis & Transformation', 'Editing', 'Comprehension Cloze', 'Antonyms & Synonyms', 'Regular & Irregular Verbs'],
    faq: [
      { q: 'Prof.Scaly สอดคล้องกับหลักสูตร MOE สิงคโปร์หรือไม่?', a: 'ใช่ ทุกคำถามฝึกฝนสร้างขึ้น (หรือคัดสรรโดยครู) เฉพาะสำหรับรูปแบบข้อสอบ PSLE — Grammar Cloze, Vocabulary Cloze, Grammar MCQ, Vocabulary MCQ, Synthesis & Transformation, Editing, Comprehension Cloze พร้อมโมดูลเฉพาะสำหรับ Antonyms & Synonyms และ Regular & Irregular Verbs' },
      { q: 'รองรับระดับชั้นประถมใดบ้าง?', a: 'ประถม 4, ประถม 5 และประถม 6 คำถามปรับความยากตามระดับชั้น และติดตามผลแยกกันในแต่ละหัวข้อและทักษะย่อย' },
      { q: 'AI สร้างคำถามได้อย่างไร?', a: 'เราใช้ GPT-5.2 พร้อม prompts ที่ครูสอนภาษาอังกฤษเขียนอย่างพิถีพิถัน ทุกคำถามปรับให้เหมาะกับรูปแบบและระดับ PSLE เมื่อบุตรหลานตอบผิด สามารถแตะ "อธิบายเพิ่มเติม" เพื่อขอให้ Prof.Scaly อธิบายในแบบเข้าใจง่าย — เหมือนมีติวเตอร์ส่วนตัวเล็กๆ ที่ทุกที่นั่ง' },
      { q: 'คุณครูหรือติวเตอร์อัพโหลดคำถามเองได้ไหม?', a: 'ได้ ครูและผู้ดูแลระบบสามารถอัพโหลดคำถามผ่านฟอร์มหรือ CSV แบบเป็นชุด คำถามที่อัพโหลดจะผสมกับคำถามที่ AI สร้างในอัตรา 50/50 ระหว่างการฝึก' },
      { q: 'ราคาเท่าไหร่? สามารถยกเลิกเมื่อไหร่ก็ได้หรือไม่?', a: 'นักเรียนทุกคนได้ 50 คำถามฝึกฟรีต่อหัวข้อ — รวมทั้ง 9 โมดูลเป็นหลายร้อยคำถามฟรี อัพเกรดเป็นแบบไม่จำกัดในหนึ่งระดับชั้นเพียง SGD $2 ต่อปี ยกเลิกได้ทุกเมื่อในบัญชีของคุณ' }
    ]
  },
  vi: {
    langLabel: 'Tiếng Việt',
    login: 'Đăng nhập',
    startFree: 'Bắt đầu miễn phí',
    moeBadge: 'Phù hợp chương trình MOE Singapore',
    foundingBannerA: '🎉 Ưu đãi Thành viên Sáng lập',
    foundingBannerB: '1.000 người đăng ký đầu tiên nhận câu hỏi luyện tập KHÔNG GIỚI HẠN trong 1 năm',
    foundingBannerC: 'chỉ với SGD 2 — cho cấp lớp bạn đăng ký.',
    foundingBannerCta: 'Nhận ngay',
    foundingBannerSpotsLeft: 'suất còn lại',
    foundingBannerUrgent: '⚠️ Sắp hết chỗ',
    foundingBannerSoldOutA: '🎉 Ưu đãi Thành viên Sáng lập đã hết',
    foundingBannerSoldOutB: 'Cảm ơn bạn! Đã trở lại giá thông thường — Tham gia Prof.Scaly ngay hôm nay.',
    foundingBannerSoldOutCta: 'Đăng ký',
    heroTitle1: 'Prof.Scaly biến việc ôn',
    heroBrand: 'tiếng Anh PSLE',
    heroTitle2: 'thành niềm vui.',
    heroSubtitle: 'Gia sư AI thân thiện dành cho học sinh lớp 4, 5 và 6. Chín mô-đun tiếng Anh PSLE, giải thích thân thiện với trẻ em ngay lập tức, và theo dõi tiến độ mà phụ huynh thực sự hiểu được.',
    ctaRegister: 'Bắt đầu miễn phí — không cần thẻ',
    ctaDemo: 'Thử một câu hỏi mẫu',
    trust50Free: '50 câu miễn phí mỗi chủ đề',
    trustLevels: 'Lớp 4 · Lớp 5 · Lớp 6',
    trustAI: 'Gia sư AI tức thì',
    howTitle: 'Cách hoạt động',
    howSubtitle: 'Ba bước đơn giản để xây dựng sự tự tin tiếng Anh PSLE cho con bạn.',
    step1Num: 'BƯỚC 1', step1Title: 'Đăng ký trong 30 giây', step1Desc: 'Tạo tài khoản miễn phí và chọn lớp của con — Lớp 4, 5 hoặc 6.',
    step2Num: 'BƯỚC 2', step2Title: 'Chọn 1 trong 9 chủ đề', step2Desc: 'Từ Grammar Cloze đến Antonyms & Synonyms — tất cả đều phù hợp chương trình MOE.',
    step3Num: 'BƯỚC 3', step3Title: 'Luyện tập với phản hồi AI tức thì', step3Desc: 'Mỗi câu trả lời sai đều có giải thích dễ hiểu và câu hỏi tiếp theo.',
    modulesTitle: '9 mô-đun tiếng Anh PSLE',
    modulesSubtitle: 'Mọi loại câu hỏi trong đề thi tiếng Anh PSLE — cộng thêm các mô-đun chuyên biệt cho các kỹ năng phụ mà học sinh thường gặp khó khăn.',
    demoBadge: 'Thử trước khi đăng ký',
    demoTitle: 'Câu hỏi kiểu PSLE thực tế',
    demoSubtitle: 'Chọn một đáp án và xem giải thích của Prof.Scaly ngay lập tức.',
    demoLabel: 'Grammar MCQ · Lớp 5',
    demoQuestion: 'By the time we reached the station, the train ______ already left.',
    demoCorrectHeader: '🎉 Làm tốt lắm!',
    demoWrongHeader: 'Cố gắng tốt — đây là mẹo:',
    demoExplanation: "'Had' là thì quá khứ hoàn thành (past perfect tense) — dùng khi một hành động trong quá khứ (tàu rời đi) xảy ra trước một hành động khác trong quá khứ (chúng ta đến). 'Has' là thì hiện tại hoàn thành, và 'have' không phù hợp với chủ ngữ số ít 'train'.",
    demoCtaMore: 'Nhận hàng trăm câu hỏi tương tự — miễn phí',
    pricingTitle: 'Giá đơn giản, minh bạch',
    pricingSubtitle: 'Bắt đầu miễn phí. Nâng cấp khi con bạn sẵn sàng luyện tập không giới hạn.',
    priceFreeLabel: 'MIỄN PHÍ', priceFreePer: '/mãi mãi', priceFreeDesc: 'Hoàn hảo để dùng thử Prof.Scaly.',
    freeFeat1: '50 câu miễn phí mỗi chủ đề', freeFeat2: 'Toàn bộ 9 mô-đun PSLE', freeFeat3: 'Giải thích của gia sư AI', freeFeat4: 'Chuỗi ngày & theo dõi tiến độ',
    priceFreeCta: 'Bắt đầu miễn phí',
    priceRecommended: 'ĐƯỢC ĐỀ XUẤT',
    pricePremiumLabel: 'PREMIUM', pricePremiumAmount: 'SGD $2', pricePremiumPer: '/năm mỗi lớp',
    pricePremiumDesc: 'Rẻ hơn một đĩa cơm gà.',
    premFeat1: 'Mọi thứ trong bản miễn phí', premFeat2: 'Câu hỏi luyện tập không giới hạn', premFeat3: 'Theo dõi kỹ năng phụ độc lập', premFeat4: 'Hỗ trợ ưu tiên',
    pricePremiumCta: 'Nâng cấp Premium',
    testimonialsTitle: 'Được phụ huynh và học sinh yêu thích',
    testimonialsSubtitle: 'Đánh giá đầu tiên từ các gia đình đang dùng Prof.Scaly.',
    testSlot1Name: 'Phụ huynh · Lớp 5', testSlot1Text: 'Thêm lời chứng thực thực sự của phụ huynh ở đây sau tháng đầu tiên.',
    testSlot2Name: 'Học sinh · Lớp 6', testSlot2Text: 'Thêm câu nói của học sinh ở đây — các em đều yêu thích linh vật Prof.Scaly.',
    testSlot3Name: 'Giáo viên gia sư', testSlot3Text: 'Thêm nhận xét của giáo viên về tính năng tải lên câu hỏi tùy chỉnh.',
    testSlotRole: '(Vị trí đánh giá)',
    faqTitle: 'Câu hỏi phụ huynh thường hỏi',
    faqSubtitle: 'Vẫn chưa chắc chắn? Nhấn Prof.Scaly ở góc dưới bên phải để trò chuyện.',
    finalCtaTitle: 'Sẵn sàng biến tiếng Anh PSLE thành niềm vui?',
    finalCtaSub: 'Tạo tài khoản miễn phí trong 30 giây — không cần thẻ tín dụng.',
    finalCtaBtn: 'Bắt đầu miễn phí với Prof.Scaly',
    footerAbout: 'Luyện tập tiếng Anh PSLE cho học sinh Singapore lớp 4, 5 và 6. Phù hợp với chương trình MOE.',
    footerProduct: 'Sản phẩm', footerCompany: 'Công ty', footerTrust: 'Tin cậy',
    footerStart: 'Bắt đầu miễn phí', footerLogin: 'Đăng nhập', footerDemo: 'Câu hỏi mẫu',
    footerContact: 'Liên hệ', footerPrivacy: 'Quyền riêng tư', footerTerms: 'Điều khoản',
    trustSecure: 'An toàn theo thiết kế', trustMoe: 'Phù hợp MOE', trustBuilt: 'Xây dựng bằng tình yêu ở SG',
    footerRights: 'Đã đăng ký bản quyền.',
    disclaimer: 'Prof.Scaly là một nền tảng giáo dục được phát triển độc lập và không liên kết hoặc được xác nhận bởi MOE hoặc SEAB.',
    founderTitle: 'Gặp gỡ nhà sáng lập',
    founderSubtitle: 'Nhà giáo dục đứng sau Prof.Scaly.',
    founderName: 'Thầy Victor Chan',
    founderRole: 'Nhà sáng lập · Gia sư tiếng Anh · Người Singapore',
    founderQualBadge: 'Graduate Diploma in TESOL · 2019',
    founderInstitution: 'London Teacher Training College',
    founderBio1: 'Victor là người Singapore, sinh ra, lớn lên và được đào tạo tại Singapore. Từ những ngày còn ngồi trên ghế nhà trường, thầy đã là một độc giả say mê, và chính tình yêu dành cho việc đọc đã dẫn thầy đến với tình yêu tiếng Anh.',
    founderBio2: 'Vào năm 2019, thầy Victor quyết định theo học chứng chỉ giảng dạy chuyên nghiệp tại London Teacher Training College, và cùng năm đó thầy đã nhận được Graduate Diploma in TESOL (giảng dạy tiếng Anh cho người nói ngôn ngữ khác).',
    founderBio3: 'Kể từ đó thầy đã dạy nhiều học sinh Tiểu học và Trung học trên khắp Singapore, kết hợp chương trình MOE với các tài liệu đọc mở rộng để giúp học sinh xây dựng sự tự tin thực sự — không chỉ là kỹ năng làm bài thi.',
    founderCertCaption: 'LTTC — Graduate Diploma in TESOL',
    founderCertCta: 'Xem chứng chỉ',
    founderCertClose: 'Đóng',
    modules: ['Grammar Cloze', 'Vocabulary Cloze', 'Grammar MCQ', 'Vocabulary MCQ', 'Synthesis & Transformation', 'Editing', 'Comprehension Cloze', 'Antonyms & Synonyms', 'Regular & Irregular Verbs'],
    faq: [
      { q: 'Prof.Scaly có phù hợp với chương trình MOE Singapore không?', a: 'Có. Mọi câu hỏi luyện tập được tạo (hoặc chọn lọc bởi giáo viên) đặc biệt cho định dạng tiếng Anh PSLE — Grammar Cloze, Vocabulary Cloze, Grammar MCQ, Vocabulary MCQ, Synthesis & Transformation, Editing, Comprehension Cloze, cộng với các mô-đun chuyên biệt cho Antonyms & Synonyms và Regular & Irregular Verbs.' },
      { q: 'Nó hỗ trợ những lớp tiểu học nào?', a: 'Lớp 4, Lớp 5 và Lớp 6. Câu hỏi được điều chỉnh theo độ khó của từng lớp, và tiến độ được theo dõi riêng cho từng chủ đề và kỹ năng phụ.' },
      { q: 'AI tạo câu hỏi như thế nào?', a: 'Chúng tôi sử dụng GPT-5.2 với các prompts được soạn thảo cẩn thận bởi các gia sư tiếng Anh. Mỗi câu hỏi đều được giới hạn trong định dạng và cấp độ PSLE. Khi con bạn trả lời sai, các em có thể nhấn "Giải thích thêm" để nhờ Prof.Scaly hướng dẫn thân thiện với trẻ — một gia sư mini riêng tại mỗi chỗ ngồi.' },
      { q: 'Giáo viên hoặc gia sư có thể tải lên câu hỏi của riêng họ không?', a: 'Có. Giáo viên và quản trị viên có thể tải lên câu hỏi qua biểu mẫu hoặc tải lên hàng loạt bằng CSV. Câu hỏi tải lên được trộn 50/50 với câu hỏi do AI tạo trong khi luyện tập.' },
      { q: 'Chi phí là bao nhiêu? Tôi có thể hủy bất cứ lúc nào không?', a: 'Mỗi học sinh được 50 câu hỏi luyện tập miễn phí cho mỗi chủ đề — đó là hàng trăm câu hỏi miễn phí trên tất cả 9 mô-đun. Nâng cấp lên luyện tập không giới hạn ở một cấp Tiểu học chỉ với SGD $2 mỗi năm. Bạn có thể hủy bất cứ lúc nào từ tài khoản của mình.' }
    ]
  }
};

const MODULE_COLORS = [
  { color: '#D1FAE5', border: '#6EE7B7' },
  { color: '#FEF3C7', border: '#FCD34D' },
  { color: '#E0E7FF', border: '#A5B4FC' },
  { color: '#FFE4E6', border: '#FDA4AF' },
  { color: '#F3E8FF', border: '#D8B4FE' },
  { color: '#DBEAFE', border: '#93C5FD' },
  { color: '#FCE7F3', border: '#F9A8D4' },
  { color: '#CCFBF1', border: '#5EEAD4' },
  { color: '#FED7AA', border: '#FDBA74' }
];

const DEMO_OPTIONS = ['has', 'had', 'have', 'was'];
const DEMO_CORRECT = 'had';

const LandingPage = () => {
  const navigate = useNavigate();
  const [demoAnswer, setDemoAnswer] = useState(null);
  const [certOpen, setCertOpen] = useState(false);
  const [lang, setLang] = useState(() => {
    if (typeof window === 'undefined') return 'en';
    // 1. Respect a previously-saved user choice
    const stored = localStorage.getItem('prof_scaly_lang');
    if (stored && ['en', 'zh', 'th', 'vi'].includes(stored)) return stored;
    // 2. Auto-detect from the browser's Accept-Language on first visit
    const nav = window.navigator || {};
    const preferences = [
      ...(Array.isArray(nav.languages) ? nav.languages : []),
      nav.language,
      nav.userLanguage
    ].filter(Boolean).map((c) => c.toLowerCase());
    for (const code of preferences) {
      if (code.startsWith('zh')) return 'zh';
      if (code.startsWith('th')) return 'th';
      if (code.startsWith('vi')) return 'vi';
      if (code.startsWith('en')) return 'en';
    }
    return 'en';
  });
  const t = T[lang];
  const isDemoCorrect = demoAnswer === DEMO_CORRECT;
  const [foundingStatus, setFoundingStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const backend = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${backend}/api/founding/status`);
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setFoundingStatus(data);
      } catch (e) {
        // silent — banner just falls back to the static copy
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('prof_scaly_lang', lang);
    const htmlLang = { en: 'en', zh: 'zh-Hans', th: 'th', vi: 'vi' }[lang] || 'en';
    document.documentElement.lang = htmlLang;
  }, [lang]);

  const toggleLang = () => setLang((l) => l); // legacy no-op — kept for API compatibility

  return (
    <div className="min-h-screen bg-white">
      {/* Founding Members flashing announcement banner */}
      {(() => {
        const isFull = foundingStatus?.is_full === true;
        const isUrgent = foundingStatus?.is_urgent === true;
        const remaining = foundingStatus?.remaining;
        const claimed = foundingStatus?.claimed;
        const cap = foundingStatus?.cap;

        // Sold-out variant — quieter styling, redirects to /register
        if (isFull) {
          return (
            <div
              className="founding-banner founding-banner-full relative overflow-hidden text-white font-bold text-center px-3 py-2.5 md:py-3 cursor-pointer select-none"
              onClick={() => navigate('/register')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/register'); }}
              data-testid="founding-banner-full"
            >
              <div className="relative flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-[12px] md:text-sm leading-snug">
                <span className="font-black uppercase tracking-wide">{t.foundingBannerSoldOutA}</span>
                <span className="hidden sm:inline text-white/70">·</span>
                <span>{t.foundingBannerSoldOutB}</span>
                <span className="ml-1 inline-flex items-center gap-1 bg-white text-slate-800 px-2.5 py-0.5 rounded-full text-[11px] md:text-xs font-black hover:bg-yellow-300 transition-colors">
                  {t.foundingBannerSoldOutCta} <ChevronRight size={12} />
                </span>
              </div>
            </div>
          );
        }

        // Normal + Urgent variants
        return (
          <div
            className={`founding-banner ${isUrgent ? 'founding-banner-urgent' : ''} relative overflow-hidden text-white font-bold text-center px-3 py-2.5 md:py-3 cursor-pointer select-none`}
            onClick={() => navigate('/register')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/register'); }}
            data-testid="founding-banner"
          >
            <span className="founding-banner-shine" aria-hidden="true" />
            <div className="relative flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-[12px] md:text-sm leading-snug">
              <span className="founding-banner-pulse font-black uppercase tracking-wide">
                {isUrgent ? t.foundingBannerUrgent : t.foundingBannerA}
              </span>
              <span className="hidden sm:inline text-white/70">·</span>
              <span>{t.foundingBannerB}</span>
              <span className="text-yellow-300 font-black">{t.foundingBannerC}</span>
              {typeof remaining === 'number' && remaining > 0 && (
                <span
                  className="inline-flex items-center gap-1 bg-black/25 border border-white/30 px-2 py-0.5 rounded-full text-[10.5px] md:text-xs font-black"
                  data-testid="founding-banner-counter"
                >
                  {claimed}/{cap} · {remaining} {t.foundingBannerSpotsLeft}
                </span>
              )}
              <span className="ml-1 inline-flex items-center gap-1 bg-white text-emerald-700 px-2.5 py-0.5 rounded-full text-[11px] md:text-xs font-black hover:bg-yellow-300 hover:text-emerald-900 transition-colors">
                {t.foundingBannerCta} <ChevronRight size={12} />
              </span>
            </div>
          </div>
        );
      })()}

      {/* Nav */}
      <nav className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
            data-testid="landing-brand"
          >
            <img src={MASCOT_URL} alt="Prof.Scaly" className="w-10 h-10 object-contain" />
            <span className="font-heading font-black text-xl text-emerald-700">Prof.Scaly</span>
          </button>
          <div className="flex items-center gap-2">
            <div className="relative flex items-center" data-testid="landing-lang-picker">
              <Languages size={14} className="absolute left-3 pointer-events-none text-slate-500" />
              <select
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                aria-label="Select language"
                className="appearance-none text-sm font-bold text-slate-700 hover:text-emerald-700 border-2 border-slate-200 hover:border-emerald-400 rounded-full pl-8 pr-3 py-1.5 bg-white cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-200"
              >
                <option value="en">English</option>
                <option value="zh">中文</option>
                <option value="th">ไทย</option>
                <option value="vi">Tiếng Việt</option>
              </select>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="text-sm font-semibold text-slate-700 hover:text-emerald-700 px-3 py-2"
              data-testid="landing-nav-login"
            >
              {t.login}
            </button>
            <button
              onClick={() => navigate('/register')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors"
              data-testid="landing-nav-register"
            >
              {t.startFree}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-teal-50 -z-10" />
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1.5 rounded-full mb-4">
              <Sparkles size={12} /> {t.moeBadge}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black text-slate-900 leading-[1.1] mb-4">
              {t.heroTitle1} <span className="text-emerald-600">{t.heroBrand}</span> {t.heroTitle2}
            </h1>
            <p className="text-lg text-slate-600 mb-6 max-w-lg">{t.heroSubtitle}</p>
            <div className="flex flex-wrap gap-3 mb-6">
              <button
                onClick={() => navigate('/register')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-base font-bold px-6 py-3.5 rounded-full transition-all shadow-lg shadow-emerald-500/30 flex items-center gap-2"
                data-testid="hero-cta-register"
              >
                {t.ctaRegister} <ChevronRight size={18} />
              </button>
              <a
                href="#demo"
                className="border-2 border-slate-300 hover:border-emerald-500 text-slate-700 hover:text-emerald-700 text-base font-bold px-6 py-3.5 rounded-full transition-colors"
                data-testid="hero-cta-demo"
              >
                {t.ctaDemo}
              </a>
            </div>
            <div className="flex items-center gap-5 text-sm text-slate-600">
              <div className="flex items-center gap-1.5"><CheckCircle size={16} className="text-emerald-600" /> {t.trust50Free}</div>
              <div className="flex items-center gap-1.5"><CheckCircle size={16} className="text-emerald-600" /> {t.trustLevels}</div>
              <div className="flex items-center gap-1.5"><CheckCircle size={16} className="text-emerald-600" /> {t.trustAI}</div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative flex items-center justify-center"
          >
            <div className="absolute inset-0 bg-emerald-200 rounded-full blur-3xl opacity-40" />
            <img
              src={MASCOT_URL}
              alt="Prof.Scaly the mascot"
              className="relative w-72 md:w-96 h-auto object-contain drop-shadow-2xl"
            />
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-white" data-testid="landing-how">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-black text-slate-900 mb-3">{t.howTitle}</h2>
            <p className="text-slate-600 max-w-xl mx-auto">{t.howSubtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { num: t.step1Num, title: t.step1Title, desc: t.step1Desc, icon: Users, color: 'bg-emerald-100 text-emerald-700' },
              { num: t.step2Num, title: t.step2Title, desc: t.step2Desc, icon: BookOpen, color: 'bg-amber-100 text-amber-700' },
              { num: t.step3Num, title: t.step3Title, desc: t.step3Desc, icon: Zap, color: 'bg-indigo-100 text-indigo-700' }
            ].map((step, i) => (
              <Card key={i} className="p-6 rounded-3xl border-2 border-slate-200 hover:border-emerald-300 transition-colors">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${step.color}`}>
                  <step.icon size={24} />
                </div>
                <div className="text-xs font-black text-slate-400 mb-1">{step.num}</div>
                <h3 className="text-xl font-heading font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 9 Topic tiles */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="landing-modules">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-black text-slate-900 mb-3">{t.modulesTitle}</h2>
            <p className="text-slate-600 max-w-xl mx-auto">{t.modulesSubtitle}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {t.modules.map((name, i) => (
              <div
                key={i}
                className="rounded-2xl border-2 px-4 py-5 text-center font-heading font-bold text-slate-800 text-sm md:text-base transition-transform hover:scale-105"
                style={{ backgroundColor: MODULE_COLORS[i].color, borderColor: MODULE_COLORS[i].border }}
              >
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Demo Question */}
      <section id="demo" className="py-16 md:py-24 bg-white" data-testid="landing-demo">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1.5 rounded-full mb-3">
              <Sparkles size={12} /> {t.demoBadge}
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-black text-slate-900 mb-3">{t.demoTitle}</h2>
            <p className="text-slate-600">{t.demoSubtitle}</p>
          </div>
          <Card className="p-6 md:p-8 rounded-3xl border-2 border-emerald-200 shadow-lg">
            <div className="text-xs font-bold text-emerald-700 mb-2 uppercase tracking-wide">{t.demoLabel}</div>
            <p className="text-lg md:text-xl text-slate-800 font-semibold mb-6 leading-relaxed">{t.demoQuestion}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6">
              {DEMO_OPTIONS.map((opt) => {
                const isSelected = demoAnswer === opt;
                const isCorrectOpt = opt === DEMO_CORRECT;
                let cls = 'border-2 border-slate-200 hover:border-emerald-400 bg-white';
                if (demoAnswer) {
                  if (isCorrectOpt) cls = 'border-2 border-emerald-500 bg-emerald-50';
                  else if (isSelected) cls = 'border-2 border-rose-400 bg-rose-50';
                  else cls = 'border-2 border-slate-100 bg-slate-50 opacity-60';
                }
                return (
                  <button
                    key={opt}
                    onClick={() => !demoAnswer && setDemoAnswer(opt)}
                    disabled={!!demoAnswer}
                    className={`px-4 py-3 rounded-2xl text-left font-semibold text-slate-800 transition-all ${cls}`}
                    data-testid={`demo-option-${opt}`}
                  >
                    {opt}
                    {demoAnswer && isCorrectOpt && <span className="ml-2 text-emerald-600">✓</span>}
                    {demoAnswer && isSelected && !isCorrectOpt && <span className="ml-2 text-rose-500">✗</span>}
                  </button>
                );
              })}
            </div>
            {demoAnswer && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-5 rounded-2xl ${isDemoCorrect ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-amber-50 border-2 border-amber-200'}`}
                data-testid="demo-explanation"
              >
                <div className="flex items-start gap-3">
                  <img src={MASCOT_URL} alt="Prof.Scaly" className="w-10 h-10 flex-shrink-0 object-contain" />
                  <div>
                    <div className="font-heading font-bold text-slate-900 mb-1">
                      {isDemoCorrect ? t.demoCorrectHeader : t.demoWrongHeader}
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{t.demoExplanation}</p>
                    <button
                      onClick={() => navigate('/register')}
                      className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-5 py-2.5 rounded-full inline-flex items-center gap-1.5"
                      data-testid="demo-cta-signup"
                    >
                      {t.demoCtaMore} <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </Card>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="landing-pricing">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-black text-slate-900 mb-3">{t.pricingTitle}</h2>
            <p className="text-slate-600">{t.pricingSubtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-8 rounded-3xl border-2 border-slate-200">
              <div className="text-sm font-bold text-slate-500 mb-2">{t.priceFreeLabel}</div>
              <div className="text-4xl font-heading font-black text-slate-900 mb-1">
                $0<span className="text-lg text-slate-400 font-bold">{t.priceFreePer}</span>
              </div>
              <p className="text-sm text-slate-600 mb-6">{t.priceFreeDesc}</p>
              <ul className="space-y-2.5 mb-6 text-sm">
                {[t.freeFeat1, t.freeFeat2, t.freeFeat3, t.freeFeat4].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-600 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="w-full border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-bold py-3 rounded-full transition-colors"
                data-testid="pricing-cta-free"
              >
                {t.priceFreeCta}
              </button>
            </Card>
            <Card className="p-8 rounded-3xl border-2 border-emerald-500 shadow-xl shadow-emerald-100 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs font-black px-3 py-1 rounded-full">
                {t.priceRecommended}
              </div>
              <div className="text-sm font-bold text-emerald-700 mb-2">{t.pricePremiumLabel}</div>
              <div className="text-4xl font-heading font-black text-slate-900 mb-1">
                {t.pricePremiumAmount}<span className="text-lg text-slate-400 font-bold">{t.pricePremiumPer}</span>
              </div>
              <p className="text-sm text-slate-600 mb-6">{t.pricePremiumDesc}</p>
              <ul className="space-y-2.5 mb-6 text-sm">
                {[t.premFeat1, t.premFeat2, t.premFeat3, t.premFeat4].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-600 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-full transition-colors shadow-lg shadow-emerald-500/30"
                data-testid="pricing-cta-premium"
              >
                {t.pricePremiumCta}
              </button>
            </Card>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-16 md:py-24 bg-white" data-testid="landing-founder">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1.5 rounded-full mb-3">
              <GraduationCap size={12} /> {t.founderInstitution}
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-black text-slate-900 mb-3">{t.founderTitle}</h2>
            <p className="text-slate-600">{t.founderSubtitle}</p>
          </div>

          <div className="grid md:grid-cols-5 gap-8 items-center">
            {/* Portrait — cropped from flyer via background-position */}
            <div className="md:col-span-2 flex justify-center md:justify-end">
              <div
                className="w-56 h-56 md:w-72 md:h-72 rounded-full border-4 border-emerald-500 shadow-xl bg-emerald-50"
                style={{
                  backgroundImage: `url(${FOUNDER_PHOTO_URL})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center top',
                  backgroundRepeat: 'no-repeat'
                }}
                role="img"
                aria-label={t.founderName}
                data-testid="founder-photo"
              />
            </div>

            {/* Bio */}
            <div className="md:col-span-3">
              <h3 className="text-2xl md:text-3xl font-heading font-black text-slate-900 mb-1" data-testid="founder-name">
                {t.founderName}
              </h3>
              <div className="text-sm font-semibold text-emerald-700 mb-3">{t.founderRole}</div>

              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-900 text-xs font-bold px-3 py-1.5 rounded-full mb-5">
                <Award size={14} className="text-amber-600" />
                <span>{t.founderQualBadge}</span>
                <span className="text-amber-700/70">·</span>
                <span className="text-amber-800">{t.founderInstitution}</span>
              </div>

              <div className="space-y-3 text-slate-700 leading-relaxed text-[15px]">
                <p>{t.founderBio1}</p>
                <p>{t.founderBio2}</p>
                <p>{t.founderBio3}</p>
              </div>

              <button
                type="button"
                onClick={() => setCertOpen(true)}
                className="mt-6 inline-flex items-center gap-3 border-2 border-emerald-200 hover:border-emerald-400 bg-white hover:bg-emerald-50 rounded-2xl pl-2 pr-4 py-2 transition-colors group"
                data-testid="founder-cert-open"
              >
                <img
                  src={FOUNDER_CERT_URL}
                  alt={t.founderCertCaption}
                  className="w-12 h-12 object-cover rounded-xl border border-slate-200"
                />
                <div className="text-left">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t.founderCertCaption}</div>
                  <div className="text-sm font-bold text-emerald-700 group-hover:text-emerald-800 flex items-center gap-1">
                    {t.founderCertCta} <ChevronRight size={14} />
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Certificate Lightbox */}
        {certOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setCertOpen(false)}
            data-testid="founder-cert-modal"
          >
            <div
              className="relative max-w-3xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setCertOpen(false)}
                aria-label={t.founderCertClose}
                className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-white text-slate-800 hover:bg-slate-100 shadow-lg flex items-center justify-center"
                data-testid="founder-cert-close"
              >
                <X size={18} />
              </button>
              <img
                src={FOUNDER_CERT_URL}
                alt={t.founderCertCaption}
                className="w-full h-auto rounded-2xl shadow-2xl bg-white"
              />
              <div className="text-center text-white/90 text-sm mt-3 font-semibold">
                {t.founderCertCaption} · {t.founderInstitution}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="landing-faq">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-black text-slate-900 mb-3">{t.faqTitle}</h2>
            <p className="text-slate-600">{t.faqSubtitle}</p>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {t.faq.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="bg-white border-2 border-slate-200 rounded-2xl px-5 data-[state=open]:border-emerald-300"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="font-heading font-bold text-slate-900 hover:no-underline py-4 text-left">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed pb-4">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-emerald-600 text-white text-center">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-heading font-black mb-3">{t.finalCtaTitle}</h2>
          <p className="text-emerald-50 mb-6">{t.finalCtaSub}</p>
          <button
            onClick={() => navigate('/register')}
            className="bg-white text-emerald-700 hover:bg-emerald-50 font-black text-base px-8 py-3.5 rounded-full transition-colors shadow-xl inline-flex items-center gap-2"
            data-testid="footer-cta-register"
          >
            {t.finalCtaBtn} <ChevronRight size={18} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-10" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-4 md:px-8 grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src={MASCOT_URL} alt="Prof.Scaly" className="w-10 h-10 object-contain bg-white rounded-full p-1" />
              <span className="font-heading font-black text-lg text-white">Prof.Scaly</span>
            </div>
            <p className="text-xs leading-relaxed">{t.footerAbout}</p>
          </div>
          <div>
            <div className="text-white font-bold text-sm mb-3">{t.footerProduct}</div>
            <ul className="space-y-1.5 text-xs">
              <li><button onClick={() => navigate('/register')} className="hover:text-white">{t.footerStart}</button></li>
              <li><button onClick={() => navigate('/login')} className="hover:text-white">{t.footerLogin}</button></li>
              <li><a href="#demo" className="hover:text-white">{t.footerDemo}</a></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-bold text-sm mb-3">{t.footerCompany}</div>
            <ul className="space-y-1.5 text-xs">
              <li><a href="mailto:hello@prof-scaly.com" className="hover:text-white">{t.footerContact}</a></li>
              <li><a href="/privacy" className="hover:text-white">{t.footerPrivacy}</a></li>
              <li><a href="/terms" className="hover:text-white">{t.footerTerms}</a></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-bold text-sm mb-3">{t.footerTrust}</div>
            <ul className="space-y-1.5 text-xs">
              <li className="flex items-center gap-1.5"><ShieldCheck size={12} /> {t.trustSecure}</li>
              <li className="flex items-center gap-1.5"><Award size={12} /> {t.trustMoe}</li>
              <li className="flex items-center gap-1.5"><Heart size={12} /> {t.trustBuilt}</li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-8 mt-8 pt-6 border-t border-slate-800 text-xs text-center">
          © {new Date().getFullYear()} Prof.Scaly. {t.footerRights}
          <p className="text-[11px] text-slate-500 mt-3 max-w-3xl mx-auto leading-relaxed" data-testid="landing-disclaimer">
            {t.disclaimer}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
